import { enumOptionsFor, isEnumProperty } from "./catalogEnumOptions";
import type { CaseFieldOption } from "../model/caseReview";
import type { CaseFieldInputKind } from "../components/field_review/CaseFieldInput";

type JsonSchema = Record<string, unknown>;

/**
 * A stored date that is not ISO (legacy free text) is read as text so the value is not lost.
 */
function isMalformedDate(schema: JsonSchema, data: unknown): boolean {
  return schema.format === "date" && typeof data === "string"
    && data !== "" && !/^\d{4}-\d{2}-\d{2}$/.test(data);
}

/** A set of true/false flags edited as one control: an object whose every member is a boolean. */
export function isCriteria(schema: JsonSchema): boolean {
  const properties = (schema as { properties?: Record<string, { type?: unknown }> }).properties;
  return typesOf(schema).includes("object") && !!properties
    && Object.values(properties).every((property) => typesOf(property).includes("boolean"));
}

/** A node's types as a list, whether the catalog wrote one (`"object"`) or several (`["object","null"]`). */
export function typesOf(schema: { type?: unknown }): unknown[] {
  return Array.isArray(schema.type) ? schema.type : [schema.type];
}

/** The kind of control a schema field is edited with, from its catalog shape and stored value. */
export function fieldInputKind(schema: JsonSchema, data: unknown): CaseFieldInputKind {
  if (schema.pattern === "^[0-9]{4}(-[0-9]{2}(-[0-9]{2})?)?$") return "partial-date";
  const source = schema["x-codelist"] as string | undefined;
  const enumField = isEnumProperty(schema);
  const types = typesOf(schema);
  // Every string is one growing text field (Raja, 2026-09-09); the catalog does not size text.
  return source || enumField ? "coded"
    : isCriteria(schema) ? "criteria"
    : types.includes("boolean") ? "boolean"
    : types.includes("number") || types.includes("integer") ? "number"
    : schema.format === "date-time" ? "datetime"
    : schema.format === "date" && !isMalformedDate(schema, data) ? "date"
    : "textarea";
}

/**
 * The options a schema field offers, from its codelist, its criteria properties, or its own enum
 * members. `undefined` for a field with no fixed set of options (plain text, number, date, …).
 *
 * A codelist-backed field with no matching entry in `contextOptions` throws by name: an `?? []`
 * here would render the field as if it legitimately had zero choices, hiding a missing codelist.
 */
export function fieldInputOptions(schema: JsonSchema, contextOptions: Record<string, CaseFieldOption[]>,
                                   data: unknown): CaseFieldOption[] | undefined {
  const source = schema["x-codelist"] as string | undefined;
  if (source) {
    const options = contextOptions[source];
    if (!options) throw new Error(`No options served for codelist ${source}`);
    return options;
  }
  if (isCriteria(schema)) {
    const properties = (schema as { properties?: Record<string, { title?: string }> }).properties ?? {};
    return Object.entries(properties).map(([id, property]) => ({ id, label: property.title ?? id }));
  }
  if (isEnumProperty(schema)) return enumOptionsFor(schema, data);
  return undefined;
}
